/**
 * Base genérica de los repos Supabase del PMIS.
 *
 * Espeja a `SpBaseRepo` (SharePoint) pero contra Postgres/Supabase usando la
 * tabla única `pmis_entities` (ver `supabaseClient.ts`). Cada repo concreto
 * pasa su `entityType` (ej. "afe") y obtiene CRUD genérico con bloqueo
 * optimista por `version`.
 *
 * El filtrado por campos no indexados se hace en memoria (igual que el
 * adaptador Dexie): `listAll()` trae todas las entidades del tipo+tenant y el
 * repo concreto aplica el `filter` sobre el resultado. Suficiente para la
 * escala de un piloto.
 */

import { supabase, TABLE, SupabaseRepoError } from "./supabaseClient";
import { OptimisticLockError } from "../../repos/BaseRepo";

export interface BaseEntityShape {
  id: string;
  tenantId: string;
  version: number;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
}

interface Row<T> {
  payload: T;
}

export class SupabaseBaseRepo<T extends BaseEntityShape> {
  /**
   * @param entityType Discriminador del tipo de entidad en `pmis_entities.entity`.
   * @param tenantId   Tenant del usuario actual (columna `tenant_id`).
   */
  constructor(
    protected entityType: string,
    protected tenantId: string,
  ) {}

  // ── Lectura ───────────────────────────────────────────────────────

  /** Trae todas las entidades del tipo para el tenant actual. */
  protected async listAll(): Promise<T[]> {
    const { data, error } = await supabase()
      .from(TABLE)
      .select("payload")
      .eq("entity", this.entityType)
      .eq("tenant_id", this.tenantId);
    if (error) throw new SupabaseRepoError(error.message, `list(${this.entityType})`);
    return ((data ?? []) as Row<T>[]).map((r) => r.payload);
  }

  /**
   * Lee todas las entidades de OTRO tipo para el tenant actual. Útil para
   * operaciones cruzadas (ej. Evento deriva su código del AFE/SO padre;
   * Acta se genera desde RDs; Comisionamiento cuenta pendientes por AFE).
   */
  protected async listOtherEntity<E>(entityType: string): Promise<E[]> {
    const { data, error } = await supabase()
      .from(TABLE)
      .select("payload")
      .eq("entity", entityType)
      .eq("tenant_id", this.tenantId);
    if (error) throw new SupabaseRepoError(error.message, `listOther(${entityType})`);
    return ((data ?? []) as { payload: E }[]).map((r) => r.payload);
  }

  /** Lee una entidad por id (verifica tenant). */
  protected async getById(id: string): Promise<T | null> {
    const { data, error } = await supabase()
      .from(TABLE)
      .select("payload")
      .eq("entity", this.entityType)
      .eq("tenant_id", this.tenantId)
      .eq("id", id)
      .maybeSingle();
    if (error) throw new SupabaseRepoError(error.message, `get(${this.entityType}, ${id})`);
    return data ? (data as Row<T>).payload : null;
  }

  // ── Escritura ─────────────────────────────────────────────────────

  /** Inserta una entidad nueva (ya debe traer id, version:1, timestamps). */
  protected async createRaw(entity: T): Promise<void> {
    const { error } = await supabase().from(TABLE).insert(this.buildRow(entity));
    if (error) throw new SupabaseRepoError(error.message, `create(${this.entityType}, ${entity.id})`);
  }

  /** Inserta muchas entidades en lotes (para seeds de catálogos). */
  protected async insertManyRaw(entities: T[]): Promise<void> {
    for (let i = 0; i < entities.length; i += 500) {
      const lote = entities.slice(i, i + 500).map((e) => this.buildRow(e));
      if (lote.length === 0) continue;
      const { error } = await supabase().from(TABLE).insert(lote);
      if (error) throw new SupabaseRepoError(error.message, `insertMany(${this.entityType})`);
    }
  }

  /**
   * Actualiza con bloqueo optimista. El guard `version = expectedVersion` se
   * aplica en el WHERE: si nadie modificó el registro, actualiza 1 fila; si
   * la versión cambió, actualiza 0 filas → `OptimisticLockError`.
   *
   * El bump de `version`/`updatedAt` ya viene hecho en `entity` por el repo
   * concreto (para incluir lógica de historial/estado).
   */
  protected async updateRaw(entity: T, expectedVersion: number): Promise<void> {
    const { data, error } = await supabase()
      .from(TABLE)
      .update({ payload: entity, version: entity.version, updated_at: entity.updatedAt })
      .eq("entity", this.entityType)
      .eq("tenant_id", this.tenantId)
      .eq("id", entity.id)
      .eq("version", expectedVersion)
      .select("id");
    if (error) throw new SupabaseRepoError(error.message, `update(${this.entityType}, ${entity.id})`);
    if (!data || data.length === 0) {
      // O no existe, o la versión cambió. Distinguimos releyendo.
      const current = await this.getById(entity.id);
      const actual = current?.version ?? -1;
      throw new OptimisticLockError(entity.id, expectedVersion, actual);
    }
  }

  /** Upsert sin check de versión (operaciones idempotentes: defaults, flags). */
  protected async putRaw(entity: T): Promise<void> {
    const { error } = await supabase()
      .from(TABLE)
      .upsert(this.buildRow(entity), { onConflict: "entity,id" });
    if (error) throw new SupabaseRepoError(error.message, `put(${this.entityType}, ${entity.id})`);
  }

  /** Borra físicamente la entidad. */
  protected async deleteById(id: string): Promise<void> {
    const { error } = await supabase()
      .from(TABLE)
      .delete()
      .eq("entity", this.entityType)
      .eq("tenant_id", this.tenantId)
      .eq("id", id);
    if (error) throw new SupabaseRepoError(error.message, `delete(${this.entityType}, ${id})`);
  }

  // ── Helpers ───────────────────────────────────────────────────────

  private buildRow(entity: T): Record<string, unknown> {
    return {
      entity: this.entityType,
      id: entity.id,
      tenant_id: entity.tenantId,
      version: entity.version,
      payload: entity,
      created_at: entity.createdAt,
      updated_at: entity.updatedAt,
    };
  }
}
